import { useEffect, useRef, useState } from "react";
import http, { formatApiErrorDetail } from "@/api";
import { PILLARS } from "@/constants";
import { Upload, FileText, Image as ImageIcon, Trash2, ExternalLink } from "lucide-react";
import { useAuth } from "@/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function bytesPretty(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function EvidencePanel({ evaluationId, supplierId }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [pillar, setPillar] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const canEdit = user && (user.role === "admin" || user.role === "evaluator");

  const reload = async () => {
    if (evaluationId) {
      const { data } = await http.get(`/evaluations/${evaluationId}/evidence`);
      setRows(data);
    } else if (supplierId) {
      const { data } = await http.get(`/suppliers/${supplierId}/evidence`);
      setRows(data);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [evaluationId, supplierId]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !evaluationId) return;
    setBusy(true); setErr("");
    try {
      const form = new FormData();
      form.append("file", file);
      if (pillar) form.append("pillar", pillar);
      if (description) form.append("description", description);
      await http.post(`/evaluations/${evaluationId}/evidence`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDescription(""); setPillar("");
      await reload();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta evidencia?")) return;
    try {
      await http.delete(`/admin/evidence/${id}`);
      await reload();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const openInNewTab = async (id, filename) => {
    try {
      const res = await fetch(`${API}/evidence/${id}/download`, {
        credentials: "include", // Send cookies with request
      });
      if (!res.ok) throw new Error("Sin acceso");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div className="sketch-border bg-[var(--cream-soft)] p-6" data-testid="evidence-panel">
      <div className="font-hand text-xl text-[var(--lala-blue)]">evidencias adjuntas</div>
      <h3 className="font-display text-2xl mb-1">Documentos y fotos</h3>
      <p className="text-xs text-[var(--ink-soft)] mb-4">
        PDF o imagen (PNG/JPEG/WEBP/HEIC), máx 10 MB por archivo.
      </p>

      {canEdit && evaluationId && (
        <div className="mb-4 grid sm:grid-cols-3 gap-2 items-end" data-testid="evidence-upload-row">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">Pilar (opcional)</label>
            <select value={pillar} onChange={(e) => setPillar(e.target.value)} className="input-field py-2 text-sm">
              <option value="">— sin pilar específico —</option>
              {PILLARS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">Descripción (opcional)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Ej: Auditoría 14-ene"
              className="input-field py-2 text-sm" />
          </div>
          <div>
            <input
              ref={fileRef} type="file" hidden
              accept=".pdf,application/pdf,image/png,image/jpeg,image/webp,image/heic,image/heif"
              onChange={onUpload}
              data-testid="evidence-file-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="btn-primary text-sm w-full justify-center"
              data-testid="evidence-upload-btn"
            >
              <Upload size={14} /> {busy ? "Subiendo…" : "Subir archivo"}
            </button>
          </div>
        </div>
      )}

      {err && <div data-testid="evidence-err" className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}

      {rows.length === 0 ? (
        <div className="text-sm text-[var(--ink-soft)] italic py-4 text-center">
          {canEdit && evaluationId ? "Aún no hay evidencias. Sube la primera." : "Sin evidencias adjuntas."}
        </div>
      ) : (
        <ul className="divide-y divide-[var(--line)]" data-testid="evidence-list">
          {rows.map((r) => {
            const isImg = r.content_type?.startsWith("image/");
            const pillarLabel = r.pillar ? (PILLARS.find((p) => p.key === r.pillar)?.label || r.pillar) : null;
            return (
              <li key={r.id} className="py-3 flex items-start gap-3" data-testid={`evidence-row-${r.id}`}>
                <div className="mt-1 text-[var(--lala-blue)]">
                  {isImg ? <ImageIcon size={20} /> : <FileText size={20} />}
                </div>
                <div className="flex-1 min-w-0">
                  <button
                    type="button"
                    onClick={() => openInNewTab(r.id, r.filename)}
                    className="text-sm font-semibold text-[var(--ink)] hover:text-[var(--lala-blue)] hover:underline text-left truncate flex items-center gap-1"
                    data-testid={`evidence-open-${r.id}`}
                  >
                    {r.filename} <ExternalLink size={12} />
                  </button>
                  <div className="text-xs text-[var(--ink-soft)] mt-0.5">
                    {bytesPretty(r.size)} · {new Date(r.uploaded_at).toLocaleDateString("es-MX")} · por {r.uploaded_by}
                    {pillarLabel && <> · <span className="text-[var(--lala-blue)] font-semibold">{pillarLabel}</span></>}
                    {r.period && <> · período {r.period}</>}
                  </div>
                  {r.description && <div className="text-xs text-[var(--ink-soft)] italic mt-0.5">{r.description}</div>}
                </div>
                {user?.role === "admin" && (
                  <button
                    onClick={() => onDelete(r.id)}
                    className="text-red-600 hover:bg-red-50 rounded p-1"
                    title="Eliminar"
                    data-testid={`evidence-delete-${r.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
